import React, { useState, useEffect } from 'react';
import { Card, Button, Form } from 'react-bootstrap';
import { format, addDays, addMonths, isWithinInterval, parseISO, isValid } from 'date-fns';
import './bookingView.css';
import DetailsView from './DetailsView';
import TimeView from './TimeView';
import CalendarView from './CalendarView';
import AppointmentConfigurationService from '../../services/appointmentConfigurationService';
import { AppointmentConfiguration, AppointmentMode } from '../../types/AppointmentConfiguration';
import { ArrowLeft } from 'lucide-react';
import type { Appointment } from '../../types/Appointment';
import AppointmentService from '../../services/appointmentService';
import { ToastType, useToast } from '../toast/ToastProvider';
import { CalendarEvent } from '../../types/CalendarEvent';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { he, enUS } from 'date-fns/locale';
import ConfirmationView from './ConfirmationView';
import { AppointmentType } from '../../types/AppointmentType';
import TimeByTypeView from './TimeByTypeView';
import { UtilsService } from '../../services/utilsService';
import WhatsAppApiService from '../../services/whatsAppApiService';

export interface TimeSlot {
  time: string;
  available: boolean;
}

export interface TimeSlotsByDuration {
  [duration: number]: TimeSlot[];
}

export interface AvailableDay {
  date: Date;
  available: boolean;
  timeSlots: TimeSlot[];
}

export interface AvailableDayByDuration {
  date: Date;
  available: boolean;
  timeSlots: TimeSlotsByDuration;
}

export interface PatientDetails {
  name: string;
  email: string;
  phone: string;
  comments: string;
}

const BookingView = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';
  const locale = i18n.language === 'he' ? he : enUS;

  // States
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [disablePrev, setDisablePrev] = useState<boolean>(true);
  const [disableNext, setDisableNext] = useState<boolean>(true);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableDays, setAvailableDays] = useState<AvailableDay[]>([]);
  //const [availableDays, setAvailableDays] = useState<(AvailableDay | AvailableDayByDuration)[]>([]);
  const [availableDaysByDuration, setAvailableDaysByDuration] = useState<AvailableDayByDuration[]>([]);
  
  const [view, setView] = useState<'calendar' | 'time' | 'details' | 'confirmation'>('calendar');
  const [customerDetails, setCustomerDetails] = useState<PatientDetails>({
    name: '',
    email: '',
    phone: '',
    comments: '',
    // sendText: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const [appointmentConfig, setAppointmentConfig] = useState<AppointmentConfiguration | null>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { lang, guid } = useParams();

  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);

  // Track window resize for responsiveness
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {

    const controller = new AbortController();
    const clinicId = sessionStorage.getItem('activeClinicId');

    const fetchAppointmentConfig = async () => {
      if (clinicId || guid) {
        try {
          let appointmentConfig = null;

          if (guid) {
            appointmentConfig = await AppointmentConfigurationService.getAppointmentConfigurationByBookingLinkId(guid, controller.signal, navigate);
          }
          else if (clinicId) {
            appointmentConfig = await AppointmentConfigurationService.getAppointmentConfiguration(clinicId, controller.signal, navigate);
          }

          setAppointmentConfig(appointmentConfig);
          setCurrentMonth(new Date());

          const start = normalizeDate(new Date(appointmentConfig.startDate));
          const end = appointmentConfig.noEndDate || !appointmentConfig.endDate ? null : normalizeDate(new Date(appointmentConfig.endDate));
          setStartDate(start);
          setEndDate(end);

        } catch (error) {
          console.error('Failed to fetch appointment config', error);
        }
      }
    };

    fetchAppointmentConfig();
  }, []); // execute just on init

  useEffect(() => {
    if (!startDate) return;

    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

    const prevDisabled = prevMonth < new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const nextDisabled = endDate ? nextMonth > new Date(endDate.getFullYear(), endDate.getMonth(), 1) : false;

    setDisablePrev(prevDisabled);
    setDisableNext(nextDisabled);
  }, [currentMonth, startDate, endDate]);

  useEffect(() => {
    if (!appointmentConfig) return;

    const fetchAvailableDays = async () => {
      
      const isFixed = appointmentConfig?.appointmentMode === AppointmentMode.FixedDuration;

      if (isFixed) {
        const days = await generateAvailableDays(currentMonth, startDate, endDate);
        setAvailableDays(days);
      }
      else {
        const daysByDuration = await generateAvailableDaysByDuration(currentMonth, startDate, endDate);
        setAvailableDaysByDuration(daysByDuration);
        // Set also availableDays to allow pass availableDays to <CalendarView
        const normalizedDays = normalizeAvailableDaysByDuration(daysByDuration);
        setAvailableDays(normalizedDays);
      }
    };

    fetchAvailableDays();
  }, [appointmentConfig, currentMonth]);

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    }
  }, [lang]);

  useEffect(() => { // set the selected appointment type to the first option
    if (!selectedType && appointmentConfig?.appointmentTypes?.length) {
      setSelectedType(appointmentConfig.appointmentTypes[0]);
    }
  }, [appointmentConfig, selectedType]);

  const isFixed = appointmentConfig?.appointmentMode === AppointmentMode.FixedDuration;

  const durationMinutes = React.useMemo(() => {
    if (isFixed) {
      return appointmentConfig?.duration ?? null;
    }
    return selectedType?.durationMinutes ?? null;
  }, [isFixed, appointmentConfig?.duration, selectedType?.durationMinutes]);

  const normalizeAvailableDaysByDuration = (daysByDuration: AvailableDayByDuration[]): AvailableDay[] => {
    return daysByDuration.map((day) => {
      const durations = Object.keys(day.timeSlots); // e.g., ["15", "30", "60"]
      const firstDuration = durations[0]; // get the first available key
      const timeSlots = day.timeSlots[Number(firstDuration)]; // convert key to number

      return {
        date: day.date,
        available: day.available,
        timeSlots: timeSlots ?? [], // fallback if undefined
      };
    });
  }

  const handleTypeSelect = (type: AppointmentType) => {
    setSelectedType(type);
  };

  const normalizeDate = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const isWeekend = (date: Date): boolean => {
    return date.getDay() === 0 || date.getDay() === 6;
  };

  const isPast = (date: Date): boolean => {
    return date < new Date();
  };

  const isInTimeRanges = (date: Date): boolean => {
    if (!appointmentConfig?.timeRanges) { return false; }
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }); // "Mon", "Tue", etc.
    return appointmentConfig?.timeRanges.some(tr => tr.day === dayName);
  };

  const generateTimeSlots = (selectedDate: Date): TimeSlot[] => {
    const slots: TimeSlot[] = [];

    if (appointmentConfig) {

      // Get day name, e.g., "Tue"
      const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'short' });

      // Find matching timeRange for the selected day
      const dayTimeRange = appointmentConfig.timeRanges.find(tr => tr.day === dayName);
      if (!dayTimeRange) {
        return slots; // No available slots for this day
      }

      // Parse fromTime and toTime
      const [fromHour, fromMinute] = dayTimeRange.fromTime.split(':').map(Number);
      const [toHour, toMinute] = dayTimeRange.toTime.split(':').map(Number);

      let current = new Date(selectedDate);
      current.setHours(fromHour, fromMinute, 0, 0);

      const end = new Date(selectedDate);
      end.setHours(toHour, toMinute, 0, 0);
      const incrementMillis = appointmentConfig.duration * 60 * 1000;

      while (current < end) {
        const timeStr = current.toTimeString().slice(0, 5); // "HH:MM"

        slots.push({
          time: timeStr,
          available: true,
        });

        current = new Date(current.getTime() + incrementMillis);
      }
    }
    return slots;
  };

  const generateAvailableDays = async (currentMonth: Date, start: Date, end: Date | null): Promise<AvailableDay[]> => {
    const availableDayList: AvailableDay[] = [];
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    let bookedAppointments = await getBookedAppointments(startOfMonth, endOfMonth); // get the all month appointments
    const bookedMap = buildBookedMap(bookedAppointments);
    
    /*
    bookedMap - key with the day, values with the all booked appointmtnts on this day
    0: {"2025-08-01" => Set(2)}
    key: "2025-08-01"
    value: Set(2) {'14:30', '16:00'}

    1: {"2025-08-02" => Set(1)}
    key: "2025-08-02"
    value: Set(1) {'12:15'}

    2: {"2025-08-03" => Set(2)}
    key: "2025-08-03"
    value: Set(1) {'15:30', '13:45'}
    */

    for (let day = 1; day <= endOfMonth.getDate(); day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const available = !isPast(date) && isInTimeRanges(date) && date >= start && (!end || date <= end);
      let timeSlots = available ? generateTimeSlots(date) : [];

      const dateKey = formatDateKey(date);

      if (available && bookedMap.has(dateKey)) {
        const bookedTimes = bookedMap.get(dateKey)!;

        // Mark booked times as unavailable
        timeSlots = timeSlots.map(slot => ({
          ...slot,
          available: !bookedTimes.has(slot.time),
        }));
      }

      availableDayList.push({
        date,
        available,
        timeSlots,
      });
    }

    return availableDayList;
  };

  const generateAvailableDaysByDuration = async (currentMonth: Date, start: Date, end: Date | null): Promise<AvailableDayByDuration[]> => {
    const availableDayList: AvailableDay[] = [];
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    let bookedAppointments = await getBookedAppointments(startOfMonth, endOfMonth); // get the all month appointments
    const bookedMap = buildBookedMapByDuration(bookedAppointments);

    /*
    bookedMap
    15: Map(3) {'2025-08-01' => Set(3), '2025-08-02' => Set(2), '2025-08-03' => Set(6)}
    30: Map(3) {'2025-08-01' => Set(2), '2025-08-02' => Set(1), '2025-08-03' => Set(5)}
    60: Map(3) {'2025-08-01' => Set(2), '2025-08-02' => Set(1), '2025-08-03' => Set(2)}
    */

    const availableDayByDuration: AvailableDayByDuration[] = [];

    for (let day = 1; day <= endOfMonth.getDate(); day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const available = !isPast(date) && isInTimeRanges(date) && date >= start && (!end || date <= end);
      let timeSlots: TimeSlotsByDuration = available ? generateTimeSlotsTypesByDuration(date) : [];

      const dateKey = formatDateKey(date);

      if (available) {
        for (const duration in timeSlots) {
          const durationKey = parseInt(duration);
          const bookedForDuration = bookedMap[durationKey];
          const bookedTimesSet = bookedForDuration?.get(dateKey);

          // Exclude booked appointments from the avaliable slots 
          if (bookedTimesSet) {
            timeSlots[durationKey] = timeSlots[durationKey].map(slot => {
              const slotStart = new Date(`${dateKey}T${slot.time}`);
              const slotEnd = new Date(slotStart.getTime() + durationKey * 60 * 1000);

              const overlaps = Array.from(bookedTimesSet).some(bookedTime => {
                const bookedStart = new Date(`${dateKey}T${bookedTime}`);
                const bookedEnd = new Date(bookedStart.getTime() + durationKey * 60 * 1000);
                return bookedStart < slotEnd && bookedEnd > slotStart;
              });

              return {
                ...slot,
                available: !overlaps,
              };
            });
          }


        }
      }

      availableDayByDuration.push({
        date,
        available,
        timeSlots,
      });
    }

    return availableDayByDuration;
  };

  const buildBookedMap = (bookedAppointments: CalendarEvent[] | undefined): Map<string, Set<string>> => {
    const bookedMap = new Map<string, Set<string>>();

    bookedAppointments?.forEach((appointment: CalendarEvent) => {
      if (appointment.date && appointment.time) {
        const dateKey = new Date(appointment.date).toISOString().split("T")[0]; // "YYYY-MM-DD"
        if (!bookedMap.has(dateKey)) {
          bookedMap.set(dateKey, new Set());
        }
        bookedMap.get(dateKey)!.add(appointment.time);
      }
    });

    return bookedMap;
  };

  type BookedMapByDuration = {
  [duration: number]: Map<string, Set<string>>;
};

  const buildBookedMapByDuration = (bookedAppointments: CalendarEvent[] | undefined): BookedMapByDuration => {
    const result: BookedMapByDuration = {};

    if (!appointmentConfig?.appointmentTypes?.length) return result;

    for (const type of appointmentConfig.appointmentTypes) { // Handle the all possible duration for example 15, 30, 60 etc.
      const duration = type.durationMinutes;
      if (!duration) continue;

      const bookedMap = new Map<string, Set<string>>();

      bookedAppointments?.forEach((appointment) => { // For each appointment
        if (!appointment.durationMinutes || !appointment.date || !appointment.time) return;

        const dateKey = new Date(appointment.date).toISOString().split("T")[0];

        if (!bookedMap.has(dateKey)) { // If hasn't key add it
          bookedMap.set(dateKey, new Set());
        }

        const bookedSet = bookedMap.get(dateKey)!;
                                                       // If current appointment duration equal or lower from the handle duration 
        if (appointment.durationMinutes <= duration) { // add the appointment to the booked appointments list (bookedSet)
          bookedSet.add(appointment.time);             
        } else { // If current appointment duration bigger then the handle duration. for exp. duration=45, appointment.durationMinutes=60 
          const incrementMinutes = 15;
          const totalIncrements = Math.floor(appointment.durationMinutes / incrementMinutes); // for example 45/15=3
          const baseTime = new Date(`${dateKey}T${appointment.time}`);

          for (let i = 0; i < totalIncrements; i++) { // 0, 1, 2
            const incrementedTime = new Date(baseTime.getTime() + i * incrementMinutes * 60 * 1000); // if for example duration=45, appointment.durationMinutes=45
            const timeString = incrementedTime.toTimeString().slice(0, 5); // "HH:mm"                // if for exp. appointment.time=16:00, 16:45, 17:30
            bookedSet.add(timeString);
          }
        }
      });

      result[duration] = bookedMap;
    }
    /*
      In this exp. if we handle slot of 15 minutes
      and the appointmtnt duration is 60 minutes
      and the appointment time is 16:00 (16:00-17:00)
      we should sign as booked also the 16:00, 16:45, 17:30
      beacuse 17:00 exist in the slot of 16:45-17:30 so we must signed more slots as booled
    */
    return result;
  };

  const generateTimeSlotsTypesByDuration = (selectedDate: Date): TimeSlotsByDuration => {
    const result: TimeSlotsByDuration = {};

    if (!appointmentConfig?.appointmentTypes?.length) return result;

    // Get day name, e.g., "Tue"
    const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'short' });

    // Find matching timeRange for the selected day
    const dayTimeRange = appointmentConfig.timeRanges.find(tr => tr.day === dayName);
    if (!dayTimeRange) { return result; }

    // Parse fromTime and toTime
    const [fromHour, fromMinute] = dayTimeRange.fromTime.split(':').map(Number);
    const [toHour, toMinute] = dayTimeRange.toTime.split(':').map(Number);

    const rangeStart = new Date(selectedDate);
    rangeStart.setHours(fromHour, fromMinute, 0, 0);

    const rangeEnd = new Date(selectedDate);
    rangeEnd.setHours(toHour, toMinute, 0, 0);

    // Loop through each appointment type (duration)
    for (const type of appointmentConfig.appointmentTypes) {
      const duration = type.durationMinutes;
      if (!duration) { continue; }

      const incrementMillis = duration * 60 * 1000;
      const current = new Date(rangeStart); // start fresh for each duration
      const slots: TimeSlot[] = [];

      while (current < rangeEnd) {
        const timeStr = current.toTimeString().slice(0, 5); // "HH:MM"

        slots.push({
          time: timeStr,
          available: true,
        });

        current.setTime(current.getTime() + incrementMillis);
      }

      result[duration] = slots;
    }

    return result;
  };

  const formatDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getBookedAppointments = async (start: Date, end: Date) => {
    const clinicId = sessionStorage.getItem('activeClinicId') ?? appointmentConfig?.clinicId ?? '';
    const controller = new AbortController();
    let appointment = null;

    if (guid) {
      appointment = await AppointmentService.getAppointmentByBookingLinkId(guid, start, end, controller.signal, navigate);
    }
    else if (clinicId) {
      appointment = await AppointmentService.getAppointment(clinicId, start, end, controller.signal, navigate);
    }

    return appointment;
  }

  // Handle month navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentMonth(addMonths(currentMonth, -1));
    } else {
      setCurrentMonth(addMonths(currentMonth, 1));
    }
    setSelectedDate(null);
  };

  // Get time slots for selected date
  const getTimeSlotsForSelectedDate = () => {
    if (!selectedDate) return [];

    //alert('getTimeSlotsForSelectedDate implement');
    const dayData = availableDays.find(
      (availableDay) => availableDay.date.getDate() === selectedDate.getDate() &&
        availableDay.date.getMonth() === selectedDate.getMonth() &&
        availableDay.date.getFullYear() === selectedDate.getFullYear()
    );
  
    return dayData?.timeSlots.filter(slot => slot.available) || [];
  };


  // Get time slots for selected date
  const getTimeSlotsForSelectedDateByDuration = (): TimeSlotsByDuration => {
    if (!selectedDate) return {};

    const dayData = availableDaysByDuration.find(
      (availableDay: any) =>
        availableDay.date.getDate() === selectedDate.getDate() &&
        availableDay.date.getMonth() === selectedDate.getMonth() &&
        availableDay.date.getFullYear() === selectedDate.getFullYear()
    );

    if (!dayData) return {};

    const { timeSlots } = dayData;

    // Filter each group of time slots
    const filtered: TimeSlotsByDuration = {};
    for (const [duration, slots] of Object.entries(timeSlots)) {
      filtered[+duration] = slots.filter((slot: { available: any; }) => slot.available);
    }

    return filtered;
  };

  // Handle view navigation
  const goToTimeView = () => {
    if (selectedDate) {
      setView('time');
    }
  };

  const goToDetailsView = () => {
    if (selectedTime) {
      setView('details');
    }
  };

  const goBack = () => {
    if (view === 'time') {
      setView('calendar');
    } else if (view === 'details') {
      setView('time');
    }
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCustomerDetails({
      ...customerDetails,
      [name]: type === 'checkbox' ? checked : value,
    });
  };


  const validatePatientDetails = (): boolean => {
    const { name, phone } = customerDetails;

    if (!name || name.trim() === '') {
      return false;
    }

    if (!phone || phone.trim() === '') {
      return false;
    }

    return true;
  };

  // Handle booking submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime) {
      showToast(t(`toastMessages.SELECT_DATE_TIME_APPOINTMENT`), ToastType.Warning);
      return;
    }

    if (!validatePatientDetails()) {
      showToast(t(`toastMessages.ENTER_NAME_AND_PHONE`), ToastType.Warning);
      return;
    }
    
    const { phone } = customerDetails;
    if (!UtilsService.validatePhoneNumber(phone)) {
      showToast(t(`toastMessages.ENTER_VALID_PHONE`), ToastType.Warning);
      return;
    }

    const controller = new AbortController();
    const clinicId = sessionStorage.getItem('activeClinicId') ?? appointmentConfig?.clinicId ?? '';

    // const isFixed = appointmentConfig?.appointmentMode === AppointmentMode.FixedDuration;
    // const durationMinutes = isFixed
    //   ? appointmentConfig?.duration != null ?  appointmentConfig.duration : null
    //   : selectedType?.durationMinutes ?? null;

    const appointmentToSave: Appointment = {
      clinicId,
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: selectedTime,
      durationMinutes,
      name: customerDetails.name,
      email: customerDetails.email,
      phone: customerDetails.phone,
      comments: customerDetails.comments,
      appointmentType: isFixed ? null : selectedType ?? null,
    };

    try {
      let newAppointment = null
      let language = lang ?? 'en';

      if (guid) {
        newAppointment = await AppointmentService.createAppointmentByBookingLinkId(guid, appointmentToSave,language, controller.signal);
      }
      else if (clinicId) {
        newAppointment = await AppointmentService.createAppointment(appointmentToSave, language, controller.signal);
      }

      if (newAppointment?.error) {
        showToast(newAppointment.message, ToastType.Warning);
        return;
      }

      setView('confirmation');
      //add billing - handlePay();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Failed to save appointment', ToastType.Error);
    }
  };

  const handlePay = () => {
    window.open(
      'https://pay.grow.link/e3107b592737ce7bc3a130e05b4633be-MzAwNDQ4Mg',
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Card className="shadow-sm border-0 appointment-customer-view">
      <Card.Header className="bg-dark border-0">
        <div className="d-flex align-items-center position-relative">
          {view !== 'calendar' && view !== 'confirmation' && (
            <div className="position-absolute start-0">
              <Button variant="link" className="text-primary p-0 text-decoration-none" onClick={goBack}>
                <ArrowLeft size={30} strokeWidth={3.5} className="text-white" />
              </Button>
            </div>
          )}

          <div className="mx-auto text-center">
            <Card.Title className="mb-0 mt-3">
              <div className="booking-view-title">
                {view === 'calendar' && t('booking-view-calendar')}
                {view === 'time' && t('booking-view-time')}
                {view === 'details' && t('booking-view-details')}
              </div>
            </Card.Title>
          </div>

          {view === 'calendar' && <div className="position-absolute end-0" style={{ width: '60px' }}></div>}
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        <div className="row g-0">

          {/* Left content area - stacks vertically on mobile, on confirmation don't display the left panel*/}
          {view !== 'confirmation' && (
            <div className="col-md-5 border-end border-md-end-0 border-bottom-md">
              <div className="p-4">
                <h5>{appointmentConfig?.treatmentTitle}</h5>
                <h3 className="mb-3">{appointmentConfig?.treatmentName}</h3>

                {(view === 'time' || view === 'details') && (
                  <>
                    <div className="d-flex align-items-center mb-2">
                      <i className={`mdi mdi-clock-outline ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                      <span>
                        {appointmentConfig?.appointmentMode === AppointmentMode.FixedDuration
                          ? `${appointmentConfig?.duration} ${t('min')}`
                          : `${selectedType?.durationMinutes} ${t('min')}`}
                      </span>
                    </div>

                    {appointmentConfig?.appointmentMode === AppointmentMode.Types && (
                      <div className="d-flex align-items-center mb-2">
                        <i className={`mdi mdi-calendar-check ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                        <span>{selectedType?.name}</span>
                      </div>
                    )}
                  </>
                )}

                {view === 'details' && selectedDate && selectedTime && (
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <i className={`mdi mdi-calendar-outline ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                      <span>
                        {selectedTime} - {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy', { locale })}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className={`mdi mdi-earth ${isRtl ? 'ms-2' : 'me-2'}`}></i>
                      <span>{t('local-time')}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

          )}

          {/* Right content area - contains calendar, time slots, or form */}
          <div className={view === 'confirmation' ? 'col-12' : 'col-md-7 viewer-height'}>
            {view === 'calendar' && (
              <CalendarView
                currentMonth={currentMonth}
                selectedDate={selectedDate}
                availableDays={availableDays}
                windowWidth={windowWidth}
                onDateSelect={setSelectedDate}
                onNavigateMonth={navigateMonth}
                onContinue={goToTimeView}
                disableNext={disableNext}
                disablePrev={disablePrev}
              />
            )}

            {view === 'time' && selectedDate && !(appointmentConfig?.appointmentMode === AppointmentMode.Types) && (
              <TimeView
                selectedDate={selectedDate}
                timeSlots={getTimeSlotsForSelectedDate()}
                selectedTime={selectedTime}
                onTimeSelect={setSelectedTime}
                onContinue={goToDetailsView}
              />
            )}

            {view === 'time' && selectedDate && (appointmentConfig?.appointmentMode === AppointmentMode.Types) && (
              <TimeByTypeView
                selectedDate={selectedDate}
                timeSlots={getTimeSlotsForSelectedDateByDuration()}
                appointmentTypes={appointmentConfig?.appointmentTypes ?? []}
                selectedType={selectedType}
                selectedTime={selectedTime}
                onTypeSelect={handleTypeSelect}
                onTimeSelect={setSelectedTime}
                onContinue={goToDetailsView}
              />


            )}


            {view === 'details' && selectedDate && selectedTime && (
              <DetailsView
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                customerDetails={customerDetails}
                onChange={handleInputChange}
                onSubmit={handleSubmit}
                showEmailField={false}
              />
            )}

            {view === 'confirmation' && selectedDate && selectedTime && (
              <ConfirmationView
                treatmentTitle={appointmentConfig?.treatmentTitle ?? ""}
                treatmentName={appointmentConfig?.treatmentName ?? ""}
                duration={durationMinutes ?? undefined}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
              />
            )}


          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BookingView;